import { DISPLAY_BOX, THUMB_BOX, type PhotoMeta } from '@mantelpiece/shared';
import { formatCoordinates, readExif } from './exif';

export interface PreparedPhoto {
  readonly meta: PhotoMeta;
  readonly display: Blob;
  readonly thumb: Blob;
  /** Object URL for the preview. Caller must revoke it. */
  readonly previewUrl: string;
}

const fitWithin = (
  width: number,
  height: number,
  box: { width: number; height: number },
): { width: number; height: number } => {
  // Never upscale — enlarging a small photo just wastes bytes and looks worse.
  const scale = Math.min(box.width / width, box.height / height, 1);
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
};

type AnyCanvas = OffscreenCanvas | HTMLCanvasElement;

const makeCanvas = (width: number, height: number): AnyCanvas => {
  if (typeof OffscreenCanvas !== 'undefined') return new OffscreenCanvas(width, height);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  return canvas;
};

const toBlob = async (canvas: AnyCanvas, quality: number): Promise<Blob> => {
  if ('convertToBlob' in canvas) {
    return canvas.convertToBlob({ type: 'image/jpeg', quality });
  }
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Canvas encoding failed'))),
      'image/jpeg',
      quality,
    );
  });
};

/**
 * Drawing a 6000px photo straight down to 1920 in one pass produces visible
 * aliasing even with high-quality smoothing on. Halving repeatedly first costs
 * a few milliseconds and removes it.
 */
const drawScaled = (
  source: CanvasImageSource,
  sourceWidth: number,
  sourceHeight: number,
  target: { width: number; height: number },
): AnyCanvas => {
  let current: CanvasImageSource = source;
  let width = sourceWidth;
  let height = sourceHeight;

  while (width > target.width * 2 && height > target.height * 2) {
    const halfWidth = Math.max(target.width, Math.round(width / 2));
    const halfHeight = Math.max(target.height, Math.round(height / 2));
    const step = makeCanvas(halfWidth, halfHeight);
    const stepCtx = step.getContext('2d') as CanvasRenderingContext2D | null;
    if (!stepCtx) break;
    stepCtx.imageSmoothingEnabled = true;
    stepCtx.imageSmoothingQuality = 'high';
    stepCtx.drawImage(current, 0, 0, halfWidth, halfHeight);
    current = step as unknown as CanvasImageSource;
    width = halfWidth;
    height = halfHeight;
  }

  const canvas = makeCanvas(target.width, target.height);
  const ctx = canvas.getContext('2d') as CanvasRenderingContext2D | null;
  if (!ctx) throw new Error('Canvas is unavailable');
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(current, 0, 0, target.width, target.height);
  return canvas;
};

const decode = async (file: File): Promise<ImageBitmap | HTMLImageElement> => {
  try {
    // `from-image` applies the EXIF orientation for us.
    return await createImageBitmap(file, { imageOrientation: 'from-image' });
  } catch {
    const url = URL.createObjectURL(file);
    try {
      const img = new Image();
      img.src = url;
      await img.decode();
      return img;
    } finally {
      URL.revokeObjectURL(url);
    }
  }
};

const sha256Hex = async (blob: Blob): Promise<string> => {
  const digest = await crypto.subtle.digest('SHA-256', await blob.arrayBuffer());
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
};

/**
 * The first usable set of coordinates in a batch, formatted for a person.
 *
 * Read up front, before anything is resized or sent, so the suggestion is in
 * front of the sender while they can still act on it — a hint that only appeared
 * once half the batch had uploaded would be no use to anybody. Most photos have
 * no fix at all by the time they have been through a messaging app, so this
 * usually walks the whole batch and answers null; each step is a 256 KB slice
 * with no decode, which is cheap enough not to matter.
 */
export const readLocationHint = async (files: readonly File[]): Promise<string | null> => {
  for (const file of files.slice(0, 8)) {
    const { latitude, longitude } = await readExif(file);
    if (latitude !== null && longitude !== null) return formatCoordinates(latitude, longitude);
  }
  return null;
};

export const ACCEPTED_TYPES = 'image/jpeg,image/png,image/webp,image/heic,image/heif';

/**
 * Everything expensive happens here, in the browser: decode, downscale, encode,
 * hash. The Worker only ever sees ~400 KB of already-correct JPEG, which keeps
 * it off the paid CPU tiers and means no image pipeline to run or pay for.
 */
export const preparePhoto = async (file: File): Promise<PreparedPhoto> => {
  const [source, exif] = await Promise.all([decode(file), readExif(file)]);

  const sourceWidth = 'width' in source ? source.width : 0;
  const sourceHeight = 'height' in source ? source.height : 0;
  if (!sourceWidth || !sourceHeight) throw new Error('That file is not a readable image');

  const displaySize = fitWithin(sourceWidth, sourceHeight, DISPLAY_BOX);
  const thumbSize = fitWithin(sourceWidth, sourceHeight, THUMB_BOX);

  const displayBlob = await toBlob(
    drawScaled(source, sourceWidth, sourceHeight, displaySize),
    0.85,
  );
  const thumbBlob = await toBlob(drawScaled(source, sourceWidth, sourceHeight, thumbSize), 0.7);

  if ('close' in source) source.close();

  return {
    meta: {
      contentHash: await sha256Hex(displayBlob),
      width: displaySize.width,
      height: displaySize.height,
      takenAt: exif.takenAt,
      caption: null,
      location: null,
      originalFilename: file.name,
    },
    display: displayBlob,
    thumb: thumbBlob,
    previewUrl: URL.createObjectURL(thumbBlob),
  };
};
